import { type ComponentType, type PropsWithChildren, type ReactElement } from 'react'
import { ParcelTapeIllustration, type SVGIllustrationProps } from '@dhl-ui/illustrations'
import type { BaseProps, MinimalSxPadding } from '../../types'
import { spacing } from '../../theme'
import { Box } from '../Box'
import { Typography, type TypographyProps } from '../Typography'
import { StackColumn } from '../Stack'
import { IconSeverity, type IconSeverityProps } from '../IconSeverity'

type BlazeSlotProps = {
  illustration?: {
    variant?: ComponentType
  } & Partial<SVGIllustrationProps>
  iconSeverity?: {
    variant: ComponentType
  } & Omit<IconSeverityProps, 'children'>
  title?: {
    /**
     * The variant of the title. Can be one of 'h1', 'h2', 'h3', 'h4', 'h5', or 'h6'.
     * @default 'h5'
     */
    variant: TypographyProps['variant'] & ('h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6')
  }
  description?: {
    /**
     * The variant of the description. Can be either 'body1' or 'body2'.
     * @default 'body1
     */
    variant: TypographyProps['variant'] & ('body1' | 'body2')
  }
}

type InnerProps = PropsWithChildren<{
  slotProps?: BlazeSlotProps
}> &
  BaseProps<HTMLElement, MinimalSxPadding>

export type BlazeProps = InnerProps &
  ({ title: string; description?: string | ReactElement } | { title?: string; description: string | ReactElement }) & {
    /**
     * The maximum width of the Blaze component. Can be a string (e.g., '480px') or a number (e.g., 480).
     * @default '480px'
     */
    maxWidth?: string | number
  }

/**
 * Blaze
 *
 * Demos:
 * - [Blaze](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/primitives-blaze--docs)
 *
 */

export const Blaze = ({ children, slotProps, title, description, sx, maxWidth, ...props }: BlazeProps) => {
  const { variant: IllustrationVariant = ParcelTapeIllustration, ...illustrationProps } = slotProps?.illustration ?? {}

  const resolvedNode = slotProps?.iconSeverity ? (
    (({ variant, ...iconProps }) => {
      const IconComponent = variant

      return (
        <IconSeverity {...iconProps}>
          <IconComponent />
        </IconSeverity>
      )
    })(slotProps.iconSeverity)
  ) : (
    <IllustrationVariant size="M" {...illustrationProps} />
  )

  return (
    <Box
      {...props}
      sx={{
        ...sx,
        p: spacing(0, 2),
        maxWidth: maxWidth || '480px',
      }}
    >
      <StackColumn gap={3} sx={{ textAlign: 'center', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex' }}>{resolvedNode}</Box>
        <StackColumn gap={0.5} sx={{ wordBreak: 'break-word' }}>
          {title && <Typography variant={slotProps?.title?.variant ?? 'h5'}>{title}</Typography>}
          {description && <Typography variant={slotProps?.description?.variant ?? 'body1'}>{description}</Typography>}
        </StackColumn>
        {children}
      </StackColumn>
    </Box>
  )
}
